<!--
 * @Author: Tahiti
 * @Date: 2025-12-18
 * @Description: 通用视频特效详情页 - 根据模板URL动态渲染
 * @FilePath: /weryai-web/pages/video-effects/[template].vue
-->
<template>
    <tool-resizable-workspace-split class="video-effects-detail-page lg:h-full min-h-0"
        :size="appStore.toolCreatorPanelWidthPercent" :collapsed="creatorPanelCollapsed" :min-panel-px="280"
        :min-content-px="560" handle-aria-label="Resize creator panel"
        @update:size="appStore.setToolCreatorPanelWidthPercent">
        <template #panel>
            <!-- 创作设置面板容器（支持收缩） -->
            <div class="creator-panel-wrapper" :class="{ 'is-collapsed': creatorPanelCollapsed }">
                <div class="setting-panel">
                    <div class="setting-content">
                        <!-- SEO H1 标签 - 优先使用 JSON 标题，视觉隐藏 -->
                        <h1 class="sr-only">{{ seoH1 }}</h1>
                        <!-- 可见标题 -->
                        <div class="hidden lg:flex items-center">
                            <ui-select-create-menu :title="t('effects.videoEffectsTitle')"></ui-select-create-menu>
                        </div>

                        <!-- 顶部预览区域 -->
                        <div class="preview-section">
                            <div class="preview-wrapper relative">
                                <div class="preview-media">
                                    <!-- 封面图片：视频未加载时显示 -->
                                    <img v-if="templateConfig.cover_url && !videoLoaded" :src="templateConfig.cover_url"
                                        class="w-full h-full object-cover rounded-lg" alt="" aria-hidden="true"
                                        role="presentation" />
                                    <!-- 视频：加载完成后显示 -->
                                    <video v-if="previewVideoUrl" v-show="videoLoaded" ref="previewVideoRef"
                                        :src="previewVideoUrl" :poster="templateConfig.cover_url ||
                                            getResourceThumbnail(previewVideoUrl)
                                            " class="w-full h-full object-cover rounded-lg" aria-hidden="true"
                                        tabindex="-1" autoplay loop muted playsinline webkit-playsinline
                                        preload="metadata" @canplay="handleVideoCanPlay" />
                                </div>
                                <div class="preview-overlay">
                                    <h3 class="preview-title">
                                        {{ templateConfig.title }}
                                    </h3>
                                </div>
                                <!-- 改变按钮 -->
                                <button class="change-btn" type="button" @click="handleChangeTemplate">
                                    {{ t('effects.change') }}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="5" height="8" viewBox="0 0 5 8"
                                        fill="none">
                                        <path
                                            d="M4.25354 3.99317C4.25354 3.81817 4.20354 3.64317 4.07854 3.51817L1.32854 0.26817C1.05354 -0.0568293 0.57854 -0.0818308 0.27854 0.16817C-0.0464595 0.44317 -0.0714606 0.91817 0.178539 1.21817L2.52854 3.99317L0.17854 6.76817C-0.0964599 7.09317 -0.0464592 7.56817 0.278541 7.81817C0.603541 8.09317 1.07854 8.04317 1.32854 7.71817L4.07854 4.46817C4.20354 4.34317 4.25354 4.16817 4.25354 3.99317Z"
                                            fill="white" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- 图片上传 -->
                        <div v-if="requiresImageUpload" class="section-block">
                            <div class="upload-grid" :class="uploadCount === 1 ? 'single' : 'multiple'">
                                <div v-for="(_, index) in uploadCount" :key="index" class="upload-item">
                                    <tool-image-upload-single v-model:image_url="uploadedImages[index]" :label="index === 0 ? t('tools.imageLabel') : ''
                                        " :is-show-info="index === 0" />
                                </div>
                            </div>
                        </div>

                        <!-- 动态提示变量 -->
                        <tool-prompt-variable-inputs v-model="promptVariableValues" :variables="promptVariables"
                            :prompt-display="promptDisplay" />

                        <!-- 试用样本图像 -->
                        <effects-sample-images v-if="sampleImages.length > 0" :images="sampleImages"
                            @select="handleSampleSelect" />

                        <!-- 宽高比选择（多个值才显示） -->
                        <tool-aspect-radio v-if="aspectRatios.length > 1" v-model:aspect_ratio="selectedAspectRatio"
                            :aspect_ratio_list="aspectRatios" class="!mt-0" />

                        <!-- 音频开关 -->
                        <tool-video-audio v-if="hasAudioOption" v-model:generate_audio="generateAudio" class="!mt-0" />

                        <!-- 公开可见性 -->
                        <tool-public-visibility v-model:public_visible="publicVisibility" class="!mt-0" />

                        <!-- 版权保护 -->
                        <tool-copyright-protection v-model:copyright_protection="copyProtection" class="!mt-0" />
                    </div>

                    <!-- 生成按钮 -->
                    <tool-credit-required class="lg:sticky lg:bottom-0 lg:py-4 lg:!mb-0 bg-[#101012] z-1"
                        :type="toolType" :create_btn_disable="!canSubmit" :loading="isProcessing"
                        workflowType="effectTemplateCreation" :et="templateConfigId" />
                </div>

                <!-- 收缩按钮 -->
                <chat-creation-panel-collapse-toggle />
            </div>
        </template>

        <!-- 右侧历史记录/灵感 -->
        <tool-right-output-plate inspirationType="VIDEO_EFFECT_TEMPLATE_CREATION" />
    </tool-resizable-workspace-split>

    <!-- 底部介绍区域（未登录且有有效 JSON 数据时显示） -->
    <LazyVisibleAiToolsBottom hydrate-on-visible v-if="!isLoggedIn && introJsonData && introData.introduce?.title" :introduce="introData.introduce"
        :selectReason="introData.selectReason" customCategory="effects" :exploreOthers="introData.exploreOthers"
        :howToUse="introData.howToUse" :questionAnswer="introData.questionAnswer" :modelIntro="introData.modelIntro"
        :btnInfo="templateConfig.title" />

    <!-- 模板选择弹框 -->
    <EffectsTemplateSelectModal v-model:visible="showTemplateModal" type="VIDEO" :tabs="categoryTabs"
        :items="allTemplates" :category-items="categoryTemplates" :loading="isLoadingTemplates"
        :has-more="hasMoreTemplates" :loading-more="isLoadingMore" @select="handleSelectNewTemplate"
        @load-more="handleLoadMoreTemplates" />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import {
    definePageMeta,
    useRoute,
    navigateTo,
    useHead,
    useRequestURL,
    useAsyncData,
    createError,
} from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useUserStore } from '~/stores/user'
import { useCreateStore } from '~/stores/create'
import PanelCollapseToggle from '~/components/chatCreation/PanelCollapseToggle.vue'
import { emitter, showLoginModal } from '~/utils/emitter'
import { consumeToolPageInsufficientCredit } from '~/utils/toolPageInsufficientCreditLock'
import { noticeMessagePopup, getResourceThumbnail, isDirectAccess } from '~/utils/common'
import { analyticsEvent } from '~/utils/log-event-func'
import {
    getEffectTemplateTagListApi,
    getEffectTemplatePageApi,
    getEffectTemplateInfoApi,
    createEffectTemplateApi,
    type EffectTemplateCreationReq,
    type EffectPromptVariable,
    type EffectTemplateConfig,
    type EffectTemplateDetail,
    type EffectTemplateIntroJson,
} from '~/api/effects'
import EffectsTemplateSelectModal from '~/components/Effects/EffectsTemplateSelectModal.vue'
import {
    appendLocaleLabelToTitle,
    clampMetaDescription,
    clampMetaTitle,
    ensureSeoSubject,
    generateHreflangLinks,
    humanizeSeoSlug,
} from '~/composables/useMultilingualSeo'
import { useAppStore } from '~/stores/app'
const route = useRoute()

definePageMeta({
    layout: 'tool',
})

const { t, locale } = useI18n()
const userStore = useUserStore()
const createStore = useCreateStore()
const appStore = useAppStore()

// 面板收缩状态
const creatorPanelCollapsed = computed(() => appStore.creatorPanelCollapsed)

// 获取模板标识
const templateSlug = computed(() => route.params.template as string)

// 详情接口将不存在模板和服务异常都包装为 500；仅在模板清单完整可用且
// 确认不存在该 slug 时才返回 404，避免后端波动影响用户正常创作。
const isTemplateConfirmedMissing = async () => {
    const pageSize = 100
    let pageNum = 1

    try {
        while (true) {
            const res = await getEffectTemplatePageApi({
                type: 'VIDEO',
                pageNum,
                pageSize,
                lang: locale.value,
            })
            if (
                res.status !== 200 ||
                !Array.isArray(res.data?.content) ||
                !Number.isFinite(Number(res.data?.total)) ||
                Number(res.data.total) <= 0
            ) {
                return false
            }
            if (
                res.data.content.some(
                    template => template.path_url === templateSlug.value
                )
            ) {
                return false
            }

            const totalPages = Number(
                res.data.total_page ??
                (res.data as { totalPages?: number }).totalPages ??
                Math.ceil(Number(res.data.total) / pageSize)
            )
            if (!Number.isInteger(totalPages) || totalPages < pageNum) {
                return false
            }
            if (pageNum >= totalPages) return true
            pageNum += 1
        }
    } catch {
        return false
    }
}

// 是否已登录
const isLoggedIn = computed(() => userStore.isLoggedIn)

// 工具类型
const toolType = 'image_to_video'

// 状态管理
const uploadedImages = ref<string[]>([])
const selectedAspectRatio = ref<string>('9:16')
const generateAudio = ref<boolean>(true)
const publicVisibility = ref<boolean>(true)
const copyProtection = ref<boolean>(false)
const isProcessing = ref<boolean>(false)

const getEffectInspirationInsId = (): string | null => {
    if (typeof window === 'undefined') return null
    // 🔥 支持两种 customType
    if (route.query?.customType !== 'inspiration' && route.query?.customType !== 'addImageFromInspiration') return null

    try {
        const storedId = localStorage.getItem('inspirationInsId')
        if (storedId) return storedId

        const inspirationData = localStorage.getItem('inspirationData')
        if (!inspirationData) return null
        const data = JSON.parse(inspirationData)
        const inspirationType = data?.type || data?.param?.type
        if (inspirationType !== 'VIDEO_EFFECT_TEMPLATE_CREATION') return null

        const insId = data?.ins_id || data?.id
        return insId ? String(insId) : null
    } catch (error) {
        return null
    }
}

const clearEffectInspirationInsId = () => {
    if (typeof window === 'undefined') return
    localStorage.removeItem('inspirationInsId')
    localStorage.removeItem('inspirationData')
}

// 🔥 处理灵感数据回填
const handleInspirationDataRewrite = () => {
    try {
        const inspirationData = localStorage.getItem('inspirationData')
        if (inspirationData) {
            const data = JSON.parse(inspirationData)
            if (data.param?.pic_urls && Array.isArray(data.param.pic_urls)) {
                // 🔥 检查是否只填充图片
                if (data.fillImageOnly) {
                    // 只填充图片，找空位填充
                    data.param.pic_urls.forEach((url: string) => {
                        const emptyIndex = uploadedImages.value.findIndex(img => !img)
                        if (emptyIndex !== -1 && !uploadedImages.value.includes(url)) {
                            uploadedImages.value[emptyIndex] = url
                        }
                    })
                } else {
                    // 完整的灵感数据：覆盖填充
                    data.param.pic_urls.forEach((url: string, index: number) => {
                        if (index < uploadedImages.value.length) {
                            uploadedImages.value[index] = url
                        }
                    })
                    // 保存 inspirationInsId
                    const inspirationType = data?.type || data?.param?.type
                    const insId = data?.ins_id || data?.id
                    if (inspirationType === 'VIDEO_EFFECT_TEMPLATE_CREATION' && insId) {
                        localStorage.setItem('inspirationInsId', String(insId))
                    }
                }
            }
            // 🔥 不在这里清除 localStorage，让 route.path 的 watch 统一清除
        }
    } catch (error) {
        console.error('解析灵感数据失败:', error)
    }
}

// 模板选择弹框
const showTemplateModal = ref(false)
const categoryTabs = ref<any[]>([])
const allTemplates = ref<any[]>([])
const categoryTemplates = ref<Record<string, any[]>>({})
const isLoadingTemplates = ref(false)
// 分页相关状态（用于"全部"分类）
const currentPage = ref(1)
const hasMoreTemplates = ref(true)
const isLoadingMore = ref(false)

// 模板详情和介绍内容必须在 SSR 阶段完成，避免 sitemap URL 首屏只输出 slug 降级文案。
const {
    data: templateDetailData,
    error: templateDetailError,
    refresh: refreshTemplateDetail,
} = await useAsyncData<EffectTemplateDetail | null>(
    `effect-template-video:${locale.value}:${templateSlug.value}`,
    async () => {
        try {
            const res = await getEffectTemplateInfoApi(
                templateSlug.value,
                locale.value
            )
            if (res.status === 1003) {
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Video effect template not found',
                })
            }
            if (
                (res.status !== 200 || !res.data) &&
                (await isTemplateConfirmedMissing())
            ) {
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Video effect template not found',
                })
            }
            if (res.status !== 200 || !res.data) {
                console.error('视频模板详情暂不可用:', res)
                return null
            }
            return res.data
        } catch (error: any) {
            if (
                error?.statusCode === 404 &&
                error?.statusMessage === 'Video effect template not found'
            ) {
                throw error
            }
            if (await isTemplateConfirmedMissing()) {
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Video effect template not found',
                })
            }
            console.error('加载视频模板详情失败:', error)
            return null
        }
    }
)

if (templateDetailError.value) {
    throw templateDetailError.value
}

const {
    data: introJsonData,
    refresh: refreshIntroJson,
} = await useAsyncData<EffectTemplateIntroJson | null>(
    `effect-template-video-intro:${locale.value}:${templateSlug.value}`,
    async () => {
        const jsonUrl = templateDetailData.value?.json_url
        if (!jsonUrl) return null

        const response = await fetch(jsonUrl)
        return response.ok
            ? ((await response.json()) as EffectTemplateIntroJson)
            : null
    }
)

const isLoadingDetail = ref(false)

// 视频是否加载完成（用于先显示封面图，视频加载完成后切换）
const videoLoaded = ref(false)
const previewVideoRef = ref<HTMLVideoElement | null>(null)

// 模板配置（优先使用接口数据）
const templateConfig = computed(() => {
    // 如果有接口数据，使用接口数据
    if (templateDetailData.value) {
        const detail = templateDetailData.value
        const firstConfig = detail.configs?.[0]

        return {
            template_id: detail.id,
            title: detail.name?.trim() || humanizeSeoSlug(templateSlug.value),
            cover_url: detail.cover_url,
            preview_video:
                detail.video_url || firstConfig?.video_urls?.[0] || '',
            has_type_selector: (detail.configs?.length || 0) > 1,
            upload_count: Math.max(firstConfig?.image_input ?? 0, 0),
            aspect_ratios: firstConfig?.aspect_ratio || [],
            has_audio_option: firstConfig?.bgm === true,
            sample_images: firstConfig?.sample_urls || [],
            configs: detail.configs,
            tags: detail.tags,
        }
    }

    // 默认配置（接口加载中或失败时使用）
    return {
        template_id: templateSlug.value,
        title: humanizeSeoSlug(templateSlug.value),
        cover_url: '',
        preview_video: '',
        has_type_selector: false,
        upload_count: 0,
        aspect_ratios: [],
        has_audio_option: false,
        sample_images: [],
    }
})

// 是否有音频选项
const hasAudioOption = computed(() => templateConfig.value.has_audio_option)

// 获取视频 URL（从 configs 的 video_urls 中获取）
const previewVideoUrl = computed(() => {
    const firstConfig = templateDetailData.value?.configs?.[0]
    return firstConfig?.video_urls?.[0] || ''
})

// 视频加载完成回调
const handleVideoCanPlay = () => {
    videoLoaded.value = true
}

// 顶部预览 warm-up：只提升 preload，不调用 load()
const warmUpPreviewOnce = () => {
    if (!import.meta.client) return
    const video = previewVideoRef.value
    if (!video) return
    if ((video.dataset as any).weryWarm === '1') return
        ; (video.dataset as any).weryWarm = '1'
    try {
        video.preload = 'auto'
    } catch {
        // ignore
    }
}

// 上传图片数量
const uploadCount = computed(() =>
    Math.max(templateConfig.value.upload_count ?? 0, 0)
)

const requiresImageUpload = computed(() => uploadCount.value > 0)

// 动态变量列表（来自 configs[0].prompt_variables）
const promptVariables = computed<EffectPromptVariable[]>(() => {
    const firstConfig = templateDetailData.value?.configs?.[0] as EffectTemplateConfig | undefined
    if (firstConfig?.prompt_customizable !== true) return []
    const variables = firstConfig?.prompt_variables
    if (!Array.isArray(variables)) return []
    return variables
        .filter(item => typeof item?.key === 'string' && item.key.trim() !== '')
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
})

const promptDisplay = computed(() => {
    const firstConfig = templateDetailData.value?.configs?.[0] as EffectTemplateConfig | undefined
    return firstConfig?.prompt_customizable === true ? firstConfig?.prompt_display || '' : ''
})

// 动态变量值
const promptVariableValues = ref<Record<string, string>>({})

const arePromptVariablesComplete = computed(() => {
    if (promptVariables.value.length === 0) return true
    return promptVariables.value.every(variable => {
        const value = promptVariableValues.value[variable.key]
        return typeof value === 'string' && value.trim() !== ''
    })
})

// 试用样本图像（过滤掉空字符串）
const sampleImages = computed(() => {
    const images = templateConfig.value.sample_images || []
    return images.filter((url: string) => url && url.trim() !== '')
})

// 宽高比列表
const aspectRatios = computed(
    () => templateConfig.value.aspect_ratios || []
)

// 模板配置ID（用于积分计算接口的 et 参数）
const templateConfigId = computed(() => {
    const configs = templateConfig.value.configs
    if (!configs || configs.length === 0) return ''
    return String(configs[0]?.id ?? '')
})

// 是否可以提交
const canSubmit = computed(() => {
    if (!templateConfigId.value) return false
    if (!arePromptVariablesComplete.value) return false
    if (!requiresImageUpload.value) return !isProcessing.value
    const allImagesUploaded = uploadedImages.value.every(img => !!img)
    return (
        allImagesUploaded &&
        !isProcessing.value
    )
})

// 底部介绍数据（从 json_url 获取）
const introData = computed(() => {
    const json = introJsonData.value

    // 如果有 JSON 数据，使用 JSON 数据
    if (json) {
        return {
            introduce: {
                title: json.introduce?.title || '',
                desc: json.introduce?.desc || '',
                medias: json.introduce?.medias || [],
            },
            selectReason: {
                title: json.select_reason?.title || '',
                btnInfo: json.select_reason?.btn_info || '',
                desc: (json.select_reason?.desc || []).map((item: any) => ({
                    ...item,
                    thumbnail_url: item.thumbnail_url || '',
                })),
            },
            howToUse: {
                title: json.how_to_use?.title || '',
                desc: json.how_to_use?.desc || '',
                img: json.how_to_use?.img || '',
                steps: json.how_to_use?.steps || [],
            },
            questionAnswer: {
                title: json.question_answer?.title || '',
                qAData: json.question_answer?.qa_data || [],
            },
            exploreOthers: {
                title: json.explore_others?.title || '',
                showTypes: (json.explore_others?.show_types || []).map(
                    item => ({
                        desc: item.desc,
                        path: `/video-effects${item.path?.startsWith('/') ? item.path : `/${item.path}`}`,
                        bg: item.icon || '',
                    })
                ),
            },
            modelIntro: {
                title: json.start_use?.title || '',
                description: '',
                buttonText: json.start_use?.btn_info || '',
                footerText: '',
            },
        }
    }

    // 默认空数据（无 JSON 时不显示介绍页）
    return {
        introduce: { title: '', desc: '', medias: [] },
        selectReason: { title: '', btnInfo: '', desc: [] },
        howToUse: { title: '', desc: '', img: '', steps: [] },
        questionAnswer: { title: '', qAData: [] },
        exploreOthers: { title: '', showTypes: [] },
        modelIntro: {
            title: '',
            description: '',
            buttonText: '',
            footerText: '',
        },
    }
})

// ==================== SEO 优化 ====================
// SEO H1 标签：优先使用 JSON 标题，降级使用多语言模板名称
const seoH1 = computed(() => {
    if (introJsonData.value?.introduce?.title) {
        return introJsonData.value.introduce.title
    }
    return `${templateConfig.value.title} - ${t('effects.seoVideoEffectGenerator')} | WeryAI`
})

// SEO 标题：优先使用 json 的 introduce.title，降级使用多语言模板名称
const seoTitle = computed(() => {
    const introTitle = (introJsonData.value?.introduce?.title || '')
        .replace(/(?:\s*\|\s*WeryAI\s*)+$/i, '')
        .trim()
    const title = introTitle ||
        `${templateConfig.value.title} - ${t('effects.videoEffectsTitle')}`
    return clampMetaTitle(
        appendLocaleLabelToTitle(`${title} | WeryAI`, locale.value)
    )
})

// SEO 描述：优先使用 json 的 introduce.desc，降级使用多语言通用描述
const seoDescription = computed(() => {
    const baseDescription = introJsonData.value?.introduce?.desc ||
        t('effects.seoVideoEffectDesc').replace(
            '{title}',
            templateConfig.value.title
        )
    const description = ensureSeoSubject(
        baseDescription,
        templateConfig.value.title
    )
    return clampMetaDescription(
        description,
        160,
        locale.value || 'en',
        locale.value !== 'en',
        templateConfig.value.title
    )
})

const hasIndexableContent = computed(() =>
    Boolean(
        templateDetailData.value?.cover_url &&
        templateConfig.value.title.trim() &&
        seoDescription.value.length >= 80
    )
)

// SEO 关键词：优先从 JSON 数据提取，降级使用多语言通用关键词
const seoKeywords = computed(() => {
    const json = introJsonData.value
    const title = templateConfig.value.title

    // 如果有 JSON 数据，从中提取关键词
    if (json) {
        const keywords: string[] = []

        // 1. JSON 标题作为核心关键词
        if (json.introduce?.title) {
            keywords.push(json.introduce.title)
        }

        // 2. 从功能特性中提取关键词
        if (json.select_reason?.desc?.length) {
            json.select_reason.desc.forEach(item => {
                if (item.title) keywords.push(item.title)
            })
        }

        // 3. 从 FAQ 问题中提取关键词（取前3个）
        if (json.question_answer?.qa_data?.length) {
            json.question_answer.qa_data.slice(0, 3).forEach(item => {
                if (item.question) {
                    // 提取问题中的核心词（去掉疑问词）
                    const cleanQuestion = item.question
                        .replace(
                            /^(如何|怎么|怎样|什么是|为什么|How to|What is|Why|Can I)/i,
                            ''
                        )
                        .trim()
                    if (cleanQuestion.length > 2 && cleanQuestion.length < 30) {
                        keywords.push(cleanQuestion)
                    }
                }
            })
        }

        // 4. 模板标签
        if (templateConfig.value.tags?.length) {
            keywords.push(...templateConfig.value.tags)
        }

        // 5. 多语言通用关键词
        keywords.push(...t('effects.seoVideoKeywords').split(', '))

        // 去重并返回
        return [...new Set(keywords)].join(', ')
    }

    // 降级：无 JSON 时使用多语言通用关键词
    const coreKeywords = [title, `${title} AI`]
    // 多语言通用关键词
    const genericKeywords = t('effects.seoVideoKeywords').split(', ')
    const tagKeywords = templateConfig.value.tags || []

    return [...coreKeywords, ...genericKeywords, ...tagKeywords].join(', ')
})

// SoftwareApplication 结构化数据（始终注入）
const softwareAppSchema = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: templateConfig.value.title,
    description: seoDescription.value,
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web',
    image: templateConfig.value.cover_url,
    offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
    },
}))

// FAQPage 结构化数据（仅当有 FAQ 数据时注入）
const faqSchema = computed(() => {
    const qaData = introJsonData.value?.question_answer?.qa_data
    if (!qaData?.length) return null

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: qaData.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
            },
        })),
    }
})

// HowTo 结构化数据（仅当有步骤数据时注入）
const howToSchema = computed(() => {
    const howToUse = introJsonData.value?.how_to_use
    if (!howToUse?.steps?.length) return null

    return {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: howToUse.title || `How to use ${templateConfig.value.title}`,
        description: howToUse.desc,
        image: howToUse.img,
        step: howToUse.steps.map((step, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: step.title,
            text: step.desc,
        })),
    }
})

// Canonical、OG、JSON-LD 与 hreflang 共用无 query 的规范 URL。
const requestUrl = useRequestURL()
const canonicalPathname = computed(() => {
    const pathname = requestUrl.pathname.replace(/\/+$/, '') || '/'
    if (pathname === '/en') return '/'
    return pathname.startsWith('/en/') ? pathname.slice(3) : pathname
})
const canonicalUrl = computed(
    () => `https://www.weryai.com${canonicalPathname.value}`
)
const isTestHost = requestUrl.hostname === 'test.weryai.com'
const hasNonSeoQuery = computed(() => requestUrl.searchParams.size > 0)
const robotsContent = computed(() =>
    isTestHost
        ? 'noindex, nofollow'
        : hasNonSeoQuery.value
            ? 'noindex, follow'
        : hasIndexableContent.value
            ? 'index, follow'
            : 'noindex, follow'
)

// 默认 OG 图片（当模板封面图未加载时使用）
const DEFAULT_OG_IMAGE =
    'https://static.weryai.com/growth/image/weryai_94499d3ef8f300f8049d532a834bcc1c.png'

// OG/Twitter 图片 URL（优先使用模板封面，降级使用默认图片）
const socialImage = computed(() => {
    return templateConfig.value.cover_url || DEFAULT_OG_IMAGE
})

// OG 图片 alt 文本
const socialImageAlt = computed(() => {
    return templateConfig.value.title
        ? `${templateConfig.value.title} - AI Video Effect`
        : 'WeryAI Video Effects'
})

// BreadcrumbList 结构化数据
const breadcrumbSchema = computed(() => {
    const baseUrl = 'https://www.weryai.com'
    const currentLocale = locale.value || 'en'
    const localePrefix = currentLocale === 'en' ? '' : `/${currentLocale}`
    const videoEffectsUrl = `${baseUrl}${localePrefix}/video-effects`

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'WeryAI',
                item: baseUrl,
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: t('effects.videoEffectsTitle'),
                item: videoEffectsUrl,
            },
            {
                '@type': 'ListItem',
                position: 3,
                name: templateConfig.value.title || 'Video Effect Template',
                item: canonicalUrl.value,
            },
        ],
    }
})

// 注入 SEO Meta 和结构化数据
useHead({
    title: seoTitle,
    meta: [
        { name: 'description', content: seoDescription },
        { name: 'keywords', content: seoKeywords },
        // Robots Meta Tag - 告诉搜索引擎索引此页面并跟踪链接
        {
            name: 'robots',
            content: robotsContent,
        },
        {
            name: 'googlebot',
            content: robotsContent,
        },
        // Open Graph
        { property: 'og:title', content: seoTitle },
        { property: 'og:description', content: seoDescription },
        { property: 'og:image', content: socialImage },
        { property: 'og:image:alt', content: socialImageAlt },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: canonicalUrl },
        { property: 'og:site_name', content: 'WeryAI' },
        {
            property: 'og:locale',
            content: computed(() => locale.value || 'en'),
        },
        // Twitter Card
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:site', content: '@weryai' },
        { name: 'twitter:title', content: seoTitle },
        { name: 'twitter:description', content: seoDescription },
        { name: 'twitter:image', content: socialImage },
        { name: 'twitter:image:alt', content: socialImageAlt },
    ],
    link: [
        // Canonical URL - 防止重复内容问题
        {
            rel: 'canonical',
            href: canonicalUrl,
        },
        // Hreflang 标签 - 支持多语言 SEO
        ...generateHreflangLinks(canonicalPathname.value),
    ],
    htmlAttrs: {
        lang: computed(() => locale.value || 'en'),
    },
    script: computed(() => {
        const scripts: Array<{ type: string; innerHTML: string }> = []

        // 始终添加 BreadcrumbList
        scripts.push({
            type: 'application/ld+json',
            innerHTML: JSON.stringify(breadcrumbSchema.value),
        })

        // 始终添加 SoftwareApplication
        if (templateConfig.value.title) {
            scripts.push({
                type: 'application/ld+json',
                innerHTML: JSON.stringify(softwareAppSchema.value),
            })
        }

        // 条件添加 FAQPage
        if (faqSchema.value) {
            scripts.push({
                type: 'application/ld+json',
                innerHTML: JSON.stringify(faqSchema.value),
            })
        }

        // 条件添加 HowTo
        if (howToSchema.value) {
            scripts.push({
                type: 'application/ld+json',
                innerHTML: JSON.stringify(howToSchema.value),
            })
        }

        return scripts
    }),
})
// ==================== SEO 优化结束 ====================

// 视频播放控制
const handleVideoPlay = async (event: Event) => {
    const video = event.target as HTMLVideoElement
    if (video && video.paused) {
        try {
            await video.play()
        } catch (error: any) {
            if (error.name !== 'AbortError') {
                console.error('视频播放失败:', error)
            }
        }
    }
}

const handleVideoPause = (event: Event) => {
    const video = event.target as HTMLVideoElement
    if (video && !video.paused) {
        video.pause()
    }
}

const refreshTemplateSeoData = async () => {
    if (isLoadingDetail.value) return

    isLoadingDetail.value = true
    try {
        await refreshTemplateDetail()
        await refreshIntroJson()
    } finally {
        isLoadingDetail.value = false
    }
}

watch([templateSlug, locale], async (next, previous) => {
    if (
        !import.meta.client ||
        (next[0] === previous[0] && next[1] === previous[1])
    ) {
        return
    }
    await refreshTemplateSeoData()
})

// 改变模板
const handleChangeTemplate = async () => {
    showTemplateModal.value = true

    // 如果还没有加载过数据，则加载
    if (categoryTabs.value.length === 0) {
        await loadTemplateData()
    }
}

// 加载模板数据
const loadTemplateData = async () => {
    isLoadingTemplates.value = true
    // 重置分页状态
    currentPage.value = 1
    hasMoreTemplates.value = true
    try {
        // 获取分类标签
        const tagRes: any = await getEffectTemplateTagListApi(
            locale.value,
            'VIDEO'
        )
        if (tagRes.status === 200 && tagRes.data) {
            const tags = tagRes.data
                .filter((item: any) => !item.status || item.status === 'ENABLE')
                .map((item: any) => ({
                    id: item.tag || String(item.id),
                    tagId: item.id,
                    name: item.name,
                }))

            // 在前面添加"全部"选项
            categoryTabs.value = [
                { id: 'all', tagId: null, name: t('tools.tabAll') },
                ...tags,
            ]

            // 先加载"全部"分类的数据（不传 tagId）
            const allRes: any = await getEffectTemplatePageApi({
                type: 'VIDEO',
                pageNum: 1,
                pageSize: 20,
                lang: locale.value,
            })
            if (allRes.status === 200 && allRes.data?.content) {
                categoryTemplates.value['all'] = allRes.data.content.map(
                    (item: any) => ({
                        id: String(item.id),
                        slug:
                            item.path_url ||
                            item.name?.toLowerCase().replace(/\s+/g, '-'),
                        cover: item.cover_url,
                        video: item.video_urls?.[0] || '',
                        title: item.name,
                        likes: 0,
                        type: item.type,
                    })
                )
            }

            // 并行加载所有分类的模板
            const promises = tags.map(async (tab: any) => {
                const res: any = await getEffectTemplatePageApi({
                    type: 'VIDEO',
                    tagId: tab.tagId,
                    pageNum: 1,
                    pageSize: 20,
                    lang: locale.value,
                })

                if (res.status === 200 && res.data?.content) {
                    categoryTemplates.value[tab.id] = res.data.content.map(
                        (item: any) => ({
                            id: String(item.id),
                            slug:
                                item.path_url ||
                                item.name?.toLowerCase().replace(/\s+/g, '-'),
                            cover: item.cover_url, // 封面图片
                            video: item.video_urls?.[0] || '', // 视频（后端还没加，先兼容）
                            title: item.name,
                            likes: 0,
                            type: item.type,
                        })
                    )
                }
            })

            await Promise.all(promises)
        }
    } catch (error) {
        console.error('加载模板数据失败:', error)
    } finally {
        isLoadingTemplates.value = false
    }
}

// 选择样本图像
const handleSampleSelect = (image: string) => {
    if (!requiresImageUpload.value) return
    // 将样本图像填充到第一个空的上传位置
    const emptyIndex = uploadedImages.value.findIndex(img => !img)
    if (emptyIndex >= 0) {
        uploadedImages.value[emptyIndex] = image
    } else {
        // 如果没有空位，替换第一个
        uploadedImages.value[0] = image
    }
}

// 上报特效详情页事件
const sendEffectsDetailsEvent = (slug: string, from: string = 'create') => {
    if (!import.meta.client) return
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
    const local_channel = localStorage.getItem('channel') || 'official'
    analyticsEvent('page_view_effects_details', {
        effect_id: slug,
        from,
        uid: userInfo?.uid,
        channel: local_channel,
    })
}



// 选择新模板
const handleSelectNewTemplate = (item: any) => {
    const slug =
        item.slug || item.title?.toLowerCase().replace(/\s+/g, '-') || item.id
    // 标记为站内跳转，避免 onMounted 中重复上报
    sessionStorage.setItem('effects_internal_nav_video', 'true')
    sendEffectsDetailsEvent(slug)
    navigateTo(`/video-effects/${slug}`)
}

// 加载更多模板（全部分类分页）
const handleLoadMoreTemplates = async () => {
    if (isLoadingMore.value || !hasMoreTemplates.value) return

    isLoadingMore.value = true
    try {
        const nextPage = currentPage.value + 1
        const res: any = await getEffectTemplatePageApi({
            type: 'VIDEO',
            pageNum: nextPage,
            pageSize: 20,
            lang: locale.value,
        })

        if (res.status === 200 && res.data?.content) {
            const newItems = res.data.content.map((item: any) => ({
                id: String(item.id),
                slug:
                    item.path_url ||
                    item.name?.toLowerCase().replace(/\s+/g, '-'),
                cover: item.cover_url,
                video: item.video_urls?.[0] || '',
                title: item.name,
                likes: 0,
                type: item.type,
            }))

            // 追加到"全部"分类
            categoryTemplates.value['all'] = [
                ...(categoryTemplates.value['all'] || []),
                ...newItems,
            ]

            currentPage.value = nextPage
            // 判断是否还有更多数据
            hasMoreTemplates.value = res.data.content.length >= 20
        } else {
            hasMoreTemplates.value = false
        }
    } catch (error) {
        console.error('加载更多模板失败:', error)
    } finally {
        isLoadingMore.value = false
    }
}

// 生成处理
const handleCreate = async () => {
    if (!userStore.isLoggedIn) {
        showLoginModal()
        return
    }

    if (!canSubmit.value) {
        const warningMessage = !arePromptVariablesComplete.value
            ? t('effects.pleaseCompletePromptVariables')
            : t('effects.pleaseUploadImage')
        noticeMessagePopup(
            warningMessage,
            t('common.tip'),
            'warning',
            3000,
            ''
        )
        return
    }

    analyticsEvent('click_tool_page_common_create', {
        tool_name: templateSlug.value,
    })

    try {
        isProcessing.value = true

        // 获取模板配置ID
        const configId = templateConfig.value.configs?.[0]?.id

        if (!configId) {
            throw new Error(t('effects.templateConfigNotFound'))
        }

        const inspirationInsId = getEffectInspirationInsId()
        const usedInspirationInsId = !!inspirationInsId
        const promptInputsPayload = Object.fromEntries(
            Object.entries(promptVariableValues.value).filter(
                ([key, value]) =>
                    typeof key === 'string' &&
                    key.trim() !== '' &&
                    typeof value === 'string' &&
                    value.trim() !== ''
            )
        )

        const requestBody: Record<string, unknown> = {
            template_config_id: configId,
            ...(requiresImageUpload.value && {
                image_url: uploadedImages.value.filter(img => !!img),
            }),
            ...(templateDetailData.value?.configs?.[0]?.prompt_customizable === true &&
                Object.keys(promptInputsPayload).length > 0 && {
                    prompt_inputs: promptInputsPayload,
                }),
            ...(aspectRatios.value.length > 0 && {
                aspect_ratio: selectedAspectRatio.value,
            }),
            bgm: generateAudio.value,
            copyright_protection: copyProtection.value,
            public_visible: publicVisibility.value,
            platform: 'WEB',
            ...(inspirationInsId ? { ins_id: inspirationInsId } : {}),
        }

        const response = await createEffectTemplateApi(
            requestBody as unknown as EffectTemplateCreationReq
        )

        if (response.status === 200 || response.success) {
            if (usedInspirationInsId) {
                clearEffectInspirationInsId()
            }
            noticeMessagePopup(
                t('effects.taskSubmitted'),
                t('common.tip'),
                'success',
                3000,
                ''
            )

            userStore.getBalance()
            emitter.emit('refresh-creation-history')

            if (appStore.isSmallScreen) {
                navigateTo('/chat?tab=creation')
            }
        } else if ([1005, 4001, 4002].includes(response.status)) {
            showLoginModal()
        } else if ([1011].includes(response.status)) {
            await consumeToolPageInsufficientCredit(createStore, response, {
                kind: 'effect_template',
                creditWorkflowKey: 'templateCreation',
                historyCategory: 'VIDEO',
                body: { ...requestBody },
                templateName: templateConfig.value.title,
            })
        } else if (response.status !== 1002) {
            throw new Error(response.message || t('common.error'))
        }
    } catch (error: any) {
        console.error('Video generation failed:', error)
        noticeMessagePopup(
            error.message || t('common.error'),
            t('common.tip'),
            'error',
            3000,
            ''
        )
    } finally {
        isProcessing.value = false
    }
}

// 定义回填函数与工作台同步（需在 onMounted 之前定义，供注册监听使用）
const syncStateToWorkbench = () => {
    emitter.emit('sync-data-to-workbench', {
        source: 'sidebar',
        template_uploaded_images: uploadedImages.value
    })
}
const skipNextSidebarSync = ref(0)
const handleWorkbenchSync = (data: any) => {
    if (data?.source === 'sidebar') return
    skipNextSidebarSync.value += 2
    if (data.template_uploaded_images && Array.isArray(data.template_uploaded_images)) {
        const newImages = data.template_uploaded_images.map((item: any) =>
            typeof item === 'string' ? item : ''
        )
        for (let i = 0; i < uploadedImages.value.length; i++) {
            if (i < newImages.length) {
                uploadedImages.value[i] = newImages[i] || ''
            } else {
                uploadedImages.value[i] = ''
            }
        }
    }
    nextTick(() => { skipNextSidebarSync.value = 0 })
}
watch(uploadedImages, () => {
    if (skipNextSidebarSync.value > 0) {
        skipNextSidebarSync.value--
        return
    }
    syncStateToWorkbench()
}, { deep: true })

// 生命周期
onMounted(async () => {
    emitter.on('click-create', handleCreate)
    emitter.on('sync-data-from-workbench', handleWorkbenchSync)
    emitter.on('workbench-mounted', syncStateToWorkbench)

    analyticsEvent('view_tool_page_common', {
        tool_name: templateSlug.value,
    })
    analyticsEvent('page_view_create', {
        creation_mode: 'video_template',
    })

    // 如果来自工作台，立即收起侧边栏（不需要等待模板加载）
    if (route.query.from === 'workbench') {
        appStore.setCreatorPanelCollapsed(true)
    }



    // 初始化上传图片数组
    uploadedImages.value = new Array(uploadCount.value).fill('')

    // 🔥 处理灵感数据回填 - 只在 onMounted 时处理一次
    // watch 会在 query 变化时调用 handleInspirationDataRewrite()
    if (route.query.customType === 'inspiration' || route.query.customType === 'addImageFromInspiration') {
        handleInspirationDataRewrite()
    }

    // 处理 workbench 预填数据（侧边栏已在上面提前收起）
    if (route.query.from === 'workbench') {
        try {
            const workbenchData = sessionStorage.getItem('workbench_template_prefill')
            if (workbenchData) {
                const data = JSON.parse(workbenchData)
                // 填充图片
                if (data.images && Array.isArray(data.images)) {
                    data.images.forEach((url: string, index: number) => {
                        if (index < uploadedImages.value.length) {
                            uploadedImages.value[index] = url
                        }
                    })
                }
                // 填充 bgm 开关
                if (typeof data.bgm === 'boolean') {
                    generateAudio.value = data.bgm
                }
                // 填充动态变量输入
                if (data.prompt_inputs && typeof data.prompt_inputs === 'object') {
                    const nextPromptInputs: Record<string, string> = {}
                    Object.entries(data.prompt_inputs).forEach(([key, value]) => {
                        if (typeof key === 'string' && key.trim() !== '') {
                            nextPromptInputs[key] = typeof value === 'string' ? value : String(value ?? '')
                        }
                    })
                    promptVariableValues.value = nextPromptInputs
                }
                // 清除数据，避免重复使用
                sessionStorage.removeItem('workbench_template_prefill')

                // 如果需要自动生成，延迟触发
                if (data.autoGenerate) {
                    setTimeout(() => {
                        handleCreate()
                    }, 100)
                }
            }
        } catch (error) {
            console.error('解析 workbench 预填数据失败:', error)
        }
    }

    // 处理移动端历史记录预填数据
    try {
        const mobilePrefillUrl = sessionStorage.getItem('mobile_prefill_image_url')
        if (mobilePrefillUrl) {
            // 填充到第一个上传框
            uploadedImages.value[0] = mobilePrefillUrl
            // 清除 sessionStorage，避免重复使用
            sessionStorage.removeItem('mobile_prefill_image_url')
            sessionStorage.removeItem('mobile_prefill_template_slug')
        }
    } catch (error) {
        console.error('解析移动端预填数据失败:', error)
    }

    // 设置默认宽高比（仅当有可选项时）
    if (aspectRatios.value.length > 0 && aspectRatios.value[0]) {
        selectedAspectRatio.value = aspectRatios.value[0]
    }
    analyticsEvent('view_tool_page_common', {
        tool_name: templateSlug.value,
    })

    // 埋点：进入特效制作页（判断是直接URL访问还是站内跳转）
    const isInternalNav = sessionStorage.getItem('effects_internal_nav_video')
    if (isInternalNav) {
        // 站内跳转，清除标记，不重复上报（跳转前已上报）
        sessionStorage.removeItem('effects_internal_nav_video')
    } else if (isDirectAccess()) {
        // 直接通过URL访问，上报 url
        sendEffectsDetailsEvent(templateSlug.value, 'url')
    }
})

onUnmounted(() => {
    emitter.off('click-create', handleCreate)
    emitter.off('sync-data-from-workbench', handleWorkbenchSync)
    emitter.off('workbench-mounted', syncStateToWorkbench)
})

// 🔥 监听路由参数变化，处理灵感数据回填
watch(
    () => [route.query?.customType, route.query?.t],
    ([newCustomType, newTimestamp]) => {
        // 当 customType 为 inspiration 或 addImageFromInspiration 且时间戳变化时，重新加载灵感数据
        if ((newCustomType === 'inspiration' || newCustomType === 'addImageFromInspiration') && newTimestamp) {
            handleInspirationDataRewrite()
        }
    },
    { immediate: false }
)

// 🔥 监听路由变化，切换页面时清除 inspirationData 缓存
watch(
    () => route.path,
    (newPath, oldPath) => {
        // 当路由路径变化时，清除缓存
        const is_inspiration = ['inspiration', 'addImageFromInspiration'].includes(route.query?.customType as string)
        if (newPath !== oldPath && oldPath && !is_inspiration) {
            try {
                localStorage.removeItem('inspirationData')
            } catch (e) {
                // 忽略错误
            }
        }
    }
)

// 监听上传数量变化
watch(uploadCount, newCount => {
    uploadedImages.value = new Array(newCount).fill('')
})

// 监听模板变化时重置视频加载状态
watch(
    () => templateDetailData.value,
    () => {
        videoLoaded.value = false
        // 模板切换时，允许下一次 warm-up 重新触发（但依旧不 load）
        if (previewVideoRef.value) {
            ; (previewVideoRef.value.dataset as any).weryWarm = '0'
        }
    }
)

watch(
    () => previewVideoUrl.value,
    () => {
        // 当 URL 更新并渲染出 video 后，做一次 warm-up（不影响 SSR）
        nextTick(() => warmUpPreviewOnce())
    }
)
</script>

<style scoped lang="less">
// 创作面板容器（支持收缩）
.creator-panel-wrapper {
    position: relative;
    flex-shrink: 0;
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    // PC 端样式
    @media (min-width: 1024px) {
        width: 424px;
        height: 100%;
        max-height: 100%;

        &.is-collapsed {
            width: 0;

            .setting-panel {
                opacity: 0;
                pointer-events: none;
                width: 0;
                padding: 0;
                overflow: hidden;
            }
        }
    }

    // 移动端样式
    @media (max-width: 1023px) {
        width: 100%;
    }
}

.video-effects-detail-page {
    min-height: 0;
    width: 100%;
    position: relative;

    @media (max-width: 767px) {
        height: auto;
        min-height: 100vh;
        padding: 8px;
    }

    @media (min-width: 768px) and (max-width: 1023px) {
        height: auto;
        min-height: 100vh;
        padding: 12px;
    }

    @media (min-width: 1024px) {
        height: 100%;
    }
}

.setting-panel {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 16px;
    border-radius: 10px;
    background: #101012;
    border-right: 1px solid #1d1d20;
    max-height: inherit;
    overflow-y: auto;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    @media (min-width: 768px) {
        padding: 24px;
    }

    @media (min-width: 1024px) {
        padding-bottom: 0;
    }

    @media (max-width: 1023px) {
        width: 100%;
    }
}

.setting-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

// SEO 隐藏 H1 样式
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.page-title {
    color: #fff;
    font-family: Roboto;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
}

.section-title {
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 12px;

    @media (min-width: 768px) {
        font-size: 16px;
    }
}

.section-block {
    margin-bottom: 0;
}

// 上传区域
.upload-grid {
    display: flex;
    gap: 12px;

    &.single {
        flex-direction: column;
    }

    &.multiple {
        flex-direction: row;
        align-items: flex-start;
    }
}

.upload-item {
    flex: 1;
}

// 预览区域
.preview-section {
    margin-bottom: 8px;
}

.preview-wrapper {
    position: relative;
    width: 100%;
    border-radius: 10px;
    overflow: hidden;
}

.change-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 73px;
    height: 28px;
    border-radius: 20px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    border: none;
    transition: background 0.2s;

    &:hover {
        background: rgba(0, 0, 0, 0.8);
    }
}

.preview-media {
    width: 100%;
    height: 240px;
    // min-height: 180px;
    // max-height: 240px;
    background: #1b1d25;
    border-radius: 10px;
    overflow: hidden;
    position: relative;

    video,
    img {
        object-fit: contain;
        color: transparent;
        font-size: 0;
        height: 240px;
    }

    img {

        &:not([src]),
        &[src=''] {
            visibility: hidden;
        }
    }
}

.preview-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
}

.preview-title {
    color: #ffffff;
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    text-align: center;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

    @media (min-width: 768px) {
        font-size: 24px;
    }
}

.change-btn {
    backdrop-filter: blur(4px);
}

// 类型选择器
.type-scroller-wrapper {
    position: relative;
}

.type-scroller {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-bottom: 8px;

    &::-webkit-scrollbar {
        display: none;
    }
}

.type-card {
    flex-shrink: 0;
    width: 102px;
    cursor: pointer;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid transparent;
    transition: all 0.3s ease;
    background: #333643;
    display: flex;
    flex-direction: column;

    &:hover {
        border-color: rgba(153, 247, 104, 0.5);
    }

    &.active {
        border-color: #99f768;
        box-shadow: 0 0 15px rgba(153, 247, 104, 0.3);
    }
}

.type-image {
    width: 100%;
    height: 80px;
    object-fit: cover;
    display: block;
    flex-shrink: 0;
}

.type-name {
    padding: 8px;
    text-align: center;
    color: #ffffff;
    font-size: 12px;
    background: #333643;
}
</style>
